import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Menu, X, User, LogOut, Calendar, Plus, Crown, Users, Settings, BarChart3, Shield, Ticket, MessageSquare, Mail } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, isPremium, isBusinessPremium, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <nav className="nav-glass sticky top-0 z-50">
      <div className="container-custom max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img 
              src="/logo.png" 
              alt="AgeForward Society" 
              className="h-16 md:h-20 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/events" 
              className={`text-sm uppercase tracking-wider transition-colors ${isActive('/events') ? 'text-gold' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="nav-events"
            >
              Events
            </Link>
            <Link 
              to="/forum" 
              className={`text-sm uppercase tracking-wider transition-colors ${isActive('/forum') ? 'text-gold' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="nav-forum"
            >
              Forum
            </Link>
            <Link 
              to="/membership" 
              className={`text-sm uppercase tracking-wider transition-colors ${isActive('/membership') ? 'text-gold' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="nav-membership"
            >
              Membership
            </Link>
            
            {user ? (
              <>
                <Link 
                  to="/dashboard" 
                  className={`text-sm uppercase tracking-wider transition-colors ${isActive('/dashboard') ? 'text-gold' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid="nav-dashboard"
                >
                  Dashboard
                </Link>
                <Button 
                  onClick={() => navigate('/create-event')}
                  className="bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest px-4 py-2 text-xs font-semibold rounded-sm"
                  data-testid="nav-create-event"
                >
                  <Plus className="w-4 h-4 mr-1" /> Create
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full relative overflow-hidden" data-testid="nav-user-menu">
                      {user.profile_picture ? (
                        <img 
                          src={user.profile_picture} 
                          alt={user.full_name} 
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <User className="w-5 h-5" />
                      )}
                      {isPremium() && (
                        <Crown className="w-3 h-3 text-gold absolute -top-1 -right-1" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2 flex items-center gap-3">
                      {user.profile_picture ? (
                        <img 
                          src={user.profile_picture} 
                          alt={user.full_name} 
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                          <User className="w-5 h-5" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium">{user.full_name}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                    {isPremium() && (
                      <div className="px-3 pb-2">
                        <span className="inline-flex items-center gap-1 text-xs text-gold">
                          <Crown className="w-3 h-3" /> Premium Member
                        </span>
                      </div>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="dropdown-profile">
                      <Settings className="w-4 h-4 mr-2" /> Profile Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="dropdown-dashboard">
                      <Calendar className="w-4 h-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/my-tickets')} data-testid="dropdown-tickets">
                      <Ticket className="w-4 h-4 mr-2" /> My Tickets
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/email-to-event')} data-testid="dropdown-email-event">
                      <Mail className="w-4 h-4 mr-2" /> Email to Event
                    </DropdownMenuItem>
                    {isPremium() && (
                      <DropdownMenuItem onClick={() => navigate('/members')} data-testid="dropdown-members">
                        <Users className="w-4 h-4 mr-2" /> Member Directory
                      </DropdownMenuItem>
                    )}
                    {isBusinessPremium() && (
                      <DropdownMenuItem onClick={() => navigate('/analytics')} data-testid="dropdown-analytics">
                        <BarChart3 className="w-4 h-4 mr-2" /> Analytics
                      </DropdownMenuItem>
                    )}
                    {isAdmin() && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="dropdown-admin">
                        <Shield className="w-4 h-4 mr-2" /> Admin Dashboard
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate('/membership')} data-testid="dropdown-membership">
                      <Crown className="w-4 h-4 mr-2" /> Membership
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="dropdown-logout">
                      <LogOut className="w-4 h-4 mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="text-sm uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="nav-login"
                >
                  Login
                </Link>
                <Button 
                  onClick={() => navigate('/register')}
                  className="bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest px-6 py-2 text-xs font-semibold rounded-sm"
                  data-testid="nav-register"
                >
                  Join
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border/50 py-4 space-y-4">
            <Link 
              to="/events" 
              className="block text-sm uppercase tracking-wider py-2"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-events"
            >
              Events
            </Link>
            <Link 
              to="/forum" 
              className="block text-sm uppercase tracking-wider py-2"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-forum"
            >
              Forum
            </Link>
            <Link 
              to="/membership" 
              className="block text-sm uppercase tracking-wider py-2"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-membership"
            >
              Membership
            </Link>
            {user ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="block text-sm uppercase tracking-wider py-2"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-dashboard"
                >
                  Dashboard
                </Link>
                <Link 
                  to="/create-event" 
                  className="block text-sm uppercase tracking-wider py-2 text-gold"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-create"
                >
                  Create Event
                </Link>
                <button 
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="block text-sm uppercase tracking-wider py-2 text-destructive"
                  data-testid="mobile-nav-logout"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="block text-sm uppercase tracking-wider py-2"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-login"
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className="block text-sm uppercase tracking-wider py-2 text-gold"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-register"
                >
                  Join
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
